import { useEffect, useState } from 'react';
import { useCurrentUser } from '../../../contexts/UserContext';
import { createMeeting } from '../../../services/meetingService';
import { getStudentsForTeacher } from '../../../services/enrollmentService';
import { getUsersByRole } from '../../../services/userService';

const DURATION_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
];

function getDefaultScheduledAt() {
  const d = new Date(Date.now() + 60 * 60 * 1000); // 1 hour from now
  d.setMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
}

export default function ScheduleMeetingModal({ onClose, onCreated, meetingType: meetingTypeProp }) {
  const currentUser = useCurrentUser();
  const isSalesperson =
    currentUser?.role === 'admin' || currentUser?.role === 'sales';
  const meetingType = meetingTypeProp ?? (isSalesperson ? 'demo' : 'class');

  // Form state
  const [studentId, setStudentId] = useState('');
  const [mentorId, setMentorId] = useState(isSalesperson ? '' : currentUser?.id || currentUser?._id || '');
  const [scheduledAt, setScheduledAt] = useState(getDefaultScheduledAt());
  const [duration, setDuration] = useState(45);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');

  // Picker data
  const [students, setStudents] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [mentorLeads, setMentorLeads] = useState([]);
  const [loadingPickers, setLoadingPickers] = useState(false);
  const [pickersError, setPickersError] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingPickers(true);

    const fetchStudents = isSalesperson
      ? getUsersByRole('student').then((d) => d.users || [])
      : getStudentsForTeacher(currentUser?.id || currentUser?._id).then(
          (d) => d.students || []
        );

    const fetches = isSalesperson
      ? [
          fetchStudents,
          getUsersByRole('teacher').then((d) => d.users || []),
          getUsersByRole('mentor-lead').then((d) => d.users || []),
        ]
      : [fetchStudents];

    Promise.all(fetches)
      .then(([studentList, mentorList, mentorLeadList]) => {
        if (cancelled) return;
        setStudents(studentList);
        if (mentorList) setMentors(mentorList);
        if (mentorLeadList) setMentorLeads(mentorLeadList);
      })
      .catch((err) => {
        if (cancelled) return;
        setPickersError(err.message || 'Unable to load users.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingPickers(false);
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    try {
      await createMeeting({
        type: meetingType,
        mentorId: isSalesperson ? mentorId : (currentUser?.id || currentUser?._id),
        studentId,
        ...(isSalesperson && { salesPersonId: currentUser?.id || currentUser?._id }),
        scheduledAt: new Date(scheduledAt).toISOString(),
        duration,
        title: title.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch (err) {
      setSubmitError(err.message || 'Unable to schedule meeting.');
    } finally {
      setSubmitting(false);
    }
  }

  const isValid =
    studentId &&
    scheduledAt &&
    duration &&
    (!isSalesperson || mentorId);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-modal-title"
      onClick={onClose}
    >
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 id="schedule-modal-title">
            {isSalesperson ? 'Schedule Demo Session' : 'Schedule Class Session'}
          </h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {loadingPickers && <p className="muted-text modal-loading">Loading…</p>}
        {pickersError && <p className="error-text">{pickersError}</p>}

        {!loadingPickers && (
          <form className="modal-form" onSubmit={handleSubmit}>

            {/* Mentor picker — only visible to salesperson */}
            {isSalesperson && (
              <div className="form-field">
                <label htmlFor="sm-mentor">Mentor</label>
                <select
                  id="sm-mentor"
                  value={mentorId}
                  onChange={(e) => setMentorId(e.target.value)}
                  required
                >
                  <option value="">Select a mentor…</option>
                  {mentors.length > 0 && (
                    <optgroup label="Mentors">
                      {mentors.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {mentorLeads.length > 0 && (
                    <optgroup label="Mentor Leads">
                      {mentorLeads.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            )}

            {/* Student picker */}
            <div className="form-field">
              <label htmlFor="sm-student">Student</label>
              <select
                id="sm-student"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                required
              >
                <option value="">Select a student…</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date & time */}
            <div className="form-field">
              <label htmlFor="sm-time">Date & Time</label>
              <input
                id="sm-time"
                type="datetime-local"
                value={scheduledAt}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => setScheduledAt(e.target.value)}
                required
              />
            </div>

            {/* Duration */}
            <div className="form-field">
              <label htmlFor="sm-duration">Duration</label>
              <select
                id="sm-duration"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Optional title */}
            <div className="form-field">
              <label htmlFor="sm-title">Title <span className="field-optional">(optional)</span></label>
              <input
                id="sm-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Auto-generated if left blank"
              />
            </div>

            {/* Optional notes */}
            <div className="form-field">
              <label htmlFor="sm-notes">Notes <span className="field-optional">(optional)</span></label>
              <textarea
                id="sm-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any prep notes or agenda items…"
              />
            </div>

            {submitError && <p className="error-text">{submitError}</p>}

            <div className="modal-actions">
              <button type="button" className="pill-btn subtle" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="pill-btn primary"
                disabled={!isValid || submitting}
              >
                {submitting ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
